import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Eye,
  GitCompare,
  Copy,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  FileEdit,
  ArrowRightLeft,
  Lightbulb,
  CalendarClock,
  Users,
  Hammer,
  ShieldAlert,
  Building2,
  FileText,
  Zap,
  Link,
  Lock,
} from 'lucide-react';
import type {
  BudgetRevision,
  CostCategoryType,
  RevisionReasonCategory,
  RevisionRoleChange,
  RevisionCategoryChange,
} from '@/types/budget.types';


// ─── Category helpers ────────────────────────────────────────────────────────

const CATEGORY_COLOURS: Record<RevisionReasonCategory | 'custom' | string, string> = {
  'scope-change':            'bg-accent-nile-50 text-accent-nile-900 border-accent-nile-500',
  'client-request':          'bg-accent-purple-50 text-accent-purple-900 border-accent-purple-500',
  'resource-substitution':   'bg-accent-orange-50 text-accent-orange-900 border-accent-orange-500',
  'rate-change':             'bg-accent-lagoon-50 text-accent-lagoon-900 border-accent-lagoon-500',
  'risk-mitigation':         'bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500',
  'estimation-correction':   'bg-accent-destructive-50 text-accent-destructive-900 border-accent-destructive-500',
  'phase-extension':         'bg-accent-blue-violet-50 text-accent-blue-violet-900 border-accent-blue-violet-500',
  'efficiency-gain':         'bg-accent-success-50 text-accent-success-900 border-accent-success-500',
  'resource-budget-cascade': 'bg-neutral-100 text-neutral-600 border-neutral-300',
  custom:                    'bg-neutral-100 text-neutral-700 border-neutral-300',
};

const CATEGORY_ICONS: Record<RevisionReasonCategory | 'custom' | string, React.ElementType> = {
  'scope-change':            ArrowRightLeft,
  'client-request':          FileEdit,
  'resource-substitution':   Users,
  'rate-change':             TrendingUp,
  'risk-mitigation':         ShieldAlert,
  'estimation-correction':   AlertTriangle,
  'phase-extension':         CalendarClock,
  'efficiency-gain':         Building2,
  'resource-budget-cascade': ArrowRightLeft,
  custom:                    Lightbulb,
};

function CategoryBadge({ category, label }: { category?: string; label?: string }) {
  if (!category) return null;
  const colourClass = CATEGORY_COLOURS[category] ?? CATEGORY_COLOURS.custom;
  const Icon = CATEGORY_ICONS[category] ?? FileText;
  const displayLabel =
    label ??
    category
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Badge variant="outline" className={`gap-1 text-xs font-medium ${colourClass}`}>
      <Icon className="h-3 w-3" />
      {displayLabel}
    </Badge>
  );
}

// ─── Role changes table ──────────────────────────────────────────────────────

function RoleChangesTable({ changes }: { changes: RevisionRoleChange[] }) {
  const significant = changes.filter((c) => c.changeType !== 'unchanged');
  if (significant.length === 0) return null;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Role</TableHead>
          <TableHead className="text-right">Before (h)</TableHead>
          <TableHead className="text-right">After (h)</TableHead>
          <TableHead className="text-right">Δ Hours</TableHead>
          <TableHead>Change</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {significant.map((change) => {
          const isAdded = change.changeType === 'added';
          const isRemoved = change.changeType === 'removed';
          const isModified = change.changeType === 'modified';
          const deltaPositive = change.hoursDelta > 0;

          return (
            <TableRow
              key={change.roleId}
              className={
                isAdded
                  ? 'bg-accent-success-50/50'
                  : isRemoved
                  ? 'bg-accent-destructive-50/50'
                  : undefined
              }
            >
              <TableCell
                className={`font-medium ${isRemoved ? 'line-through text-muted-foreground' : ''}`}
              >
                {change.role}
              </TableCell>
              <TableCell className="text-right text-muted-foreground">
                {isAdded ? '—' : change.previousHours.toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                {isRemoved ? '—' : change.newHours.toLocaleString()}
              </TableCell>
              <TableCell
                className={`text-right font-medium ${
                  deltaPositive ? 'text-accent-warning-900' : 'text-accent-success-900'
                }`}
              >
                {change.hoursDelta > 0 ? '+' : ''}
                {change.hoursDelta.toLocaleString()}h
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    isAdded
                      ? 'bg-accent-success-50 text-accent-success-900 border-accent-success-500'
                      : isRemoved
                      ? 'bg-accent-destructive-50 text-accent-destructive-900 border-accent-destructive-500'
                      : isModified
                      ? 'bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500'
                      : 'text-muted-foreground'
                  }`}
                >
                  {isAdded ? 'Added' : isRemoved ? 'Removed' : 'Modified'}
                </Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ─── Category changes table (cost / sales layers) ────────────────────────────

const TYPE_ORDER: CostCategoryType[] = ['labour', 'expenses', 'subcontractors'];
const TYPE_LABELS: Record<CostCategoryType, string> = {
  labour: 'Labour',
  expenses: 'Expenses',
  subcontractors: 'Subcontractors',
  materials: 'Materials',
  equipment: 'Equipment',
  overhead: 'Overhead',
  contingency: 'Contingency',
};

// Aggregate all labour-type rows into a single synthetic "Labour" row, matching
// the edit screen's locked Labour group row. Non-labour rows pass through unchanged.
function aggregateLabourRows(changes: RevisionCategoryChange[]): RevisionCategoryChange[] {
  const labourRows = changes.filter((c) => c.type === 'labour');
  const nonLabourRows = changes.filter((c) => c.type !== 'labour');

  if (labourRows.length === 0) return nonLabourRows;

  // Determine the aggregate change type: if any row changed, use the dominant direction
  const hasIncrease = labourRows.some((c) => c.changeType === 'increased' || c.changeType === 'added');
  const hasDecrease = labourRows.some((c) => c.changeType === 'decreased' || c.changeType === 'removed');
  const allUnchanged = labourRows.every((c) => c.changeType === 'unchanged');

  let changeType: RevisionCategoryChange['changeType'] = 'unchanged';
  if (!allUnchanged) {
    const totalDelta = labourRows.reduce((s, c) => s + c.budgetDelta, 0);
    changeType = totalDelta > 0 ? 'increased' : totalDelta < 0 ? 'decreased' : 'unchanged';
    // If rows were added/removed (not just value changes), prefer that label
    if (!hasIncrease && !hasDecrease) changeType = 'unchanged';
  }

  const syntheticLabour: RevisionCategoryChange = {
    categoryId: -1, // synthetic
    categoryName: 'Labour',
    type: 'labour',
    previousBudget: labourRows.reduce((s, c) => s + c.previousBudget, 0),
    newBudget: labourRows.reduce((s, c) => s + c.newBudget, 0),
    budgetDelta: labourRows.reduce((s, c) => s + c.budgetDelta, 0),
    percentChange: 0, // recalculate below
    changeType,
  };

  const prev = syntheticLabour.previousBudget;
  syntheticLabour.percentChange =
    prev > 0 ? Math.round((syntheticLabour.budgetDelta / prev) * 100 * 10) / 10 : 0;

  return [syntheticLabour, ...nonLabourRows];
}

function CategoryChangesTable({ changes }: { changes: RevisionCategoryChange[] }) {
  // Aggregate labour rows into a single "Labour" row, then filter unchanged
  const aggregated = aggregateLabourRows(changes);
  const significant = aggregated.filter((c) => c.changeType !== 'unchanged');
  if (significant.length === 0) return null;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Category</TableHead>
          <TableHead className="text-right">Before (£)</TableHead>
          <TableHead className="text-right">After (£)</TableHead>
          <TableHead className="text-right">Δ £</TableHead>
          <TableHead>Change</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {significant.map((change) => {
          const isAdded = change.changeType === 'added';
          const isRemoved = change.changeType === 'removed';
          const deltaPositive = change.budgetDelta > 0;
          const isLabourGroup = change.categoryId === -1;

          return (
            <TableRow
              key={change.categoryId}
              className={
                isLabourGroup
                  ? 'bg-muted/40 hover:bg-muted/50'
                  : isAdded
                  ? 'bg-accent-success-50/50'
                  : isRemoved
                  ? 'bg-accent-destructive-50/50'
                  : undefined
              }
            >
              <TableCell
                className={`font-medium ${isRemoved ? 'line-through text-muted-foreground' : ''}`}
              >
                <div className="flex items-center gap-1.5">
                  {isLabourGroup && <Lock className="h-3 w-3 text-muted-foreground" />}
                  {change.categoryName}
                </div>
              </TableCell>
              <TableCell className="text-right text-muted-foreground">
                {isAdded ? '—' : `£${change.previousBudget.toLocaleString()}`}
              </TableCell>
              <TableCell className="text-right">
                {isRemoved ? '—' : `£${change.newBudget.toLocaleString()}`}
              </TableCell>
              <TableCell
                className={`text-right font-medium ${
                  deltaPositive ? 'text-accent-warning-900' : 'text-accent-success-900'
                }`}
              >
                {change.budgetDelta > 0 ? '+' : ''}
                {`£${change.budgetDelta.toLocaleString()}`}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    isAdded
                      ? 'bg-accent-success-50 text-accent-success-900 border-accent-success-500'
                      : isRemoved
                      ? 'bg-accent-destructive-50 text-accent-destructive-900 border-accent-destructive-500'
                      : change.changeType === 'increased' || change.changeType === 'decreased'
                      ? 'bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500'
                      : 'text-muted-foreground'
                  }`}
                >
                  {isAdded
                    ? 'Added'
                    : isRemoved
                    ? 'Removed'
                    : change.changeType === 'increased'
                    ? 'Increased'
                    : 'Decreased'}
                </Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface RevisionAuditCardProps {
  revision: BudgetRevision;
  /** Controls delta unit (h vs £), visible sections, and cascade pill behaviour. Defaults to 'resource'. */
  budgetLayer?: 'resource' | 'cost' | 'sales';
  isCurrent?: boolean;
  onView: () => void;
  onCompare: () => void;
  onRestore: () => void;
  onMakeActive?: () => void;
  onDuplicate?: () => void;
}

export function RevisionAuditCard({
  revision,
  budgetLayer = 'resource',
  isCurrent = false,
  onView,
  onCompare,
  onRestore,
  onMakeActive,
  onDuplicate,
}: RevisionAuditCardProps) {
  const [roleChangesOpen, setRoleChangesOpen] = useState(false);
  const [categoryChangesOpen, setCategoryChangesOpen] = useState(false);

  const isCostSales = budgetLayer === 'cost' || budgetLayer === 'sales';

  const date = new Date(revision.createdAt ?? revision.date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const author = revision.createdBy ?? revision.changedByName ?? 'Unknown';

  const hasRoleChanges =
    !isCostSales &&
    !!revision.roleChanges &&
    revision.roleChanges.some((c) => c.changeType !== 'unchanged');

  const hasCategoryChanges =
    isCostSales &&
    !!revision.categoryChanges &&
    revision.categoryChanges.some((c) => c.changeType !== 'unchanged');

  const hoursDelta = revision.hoursChange ?? 0;
  const budgetDelta = revision.budgetChange ?? 0;
  const isAutoCascade = revision.cascadeSource === true;
  const linkedRBId = revision.linkedResourceRevisionId;
  const cascade = revision.cascadeRecord;

  return (
    <Card
      className={`${
        isCurrent ? 'border-2 border-primary bg-primary/5' : ''
      } ${
        revision.status === 'draft' ? 'border-dashed border-accent-warning-500 bg-accent-warning-50/30' : ''
      }`}
    >
      <CardContent className="pt-5 space-y-4">
        {/* ── Header row ── */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Version badge */}
            <span className="font-bold text-base text-foreground">
              {revision.revisionNumber}
            </span>

            {/* Draft badge */}
            {revision.status === 'draft' && (
              <Badge variant="outline" className="text-xs bg-accent-warning-50 text-accent-warning-900 border-accent-warning-500">
                Draft
              </Badge>
            )}

            {/* Current indicator */}
            {isCurrent && (
              <Badge className="text-xs bg-success-100 text-success-700 border-success-200" variant="outline">
                Current
              </Badge>
            )}

            {/* Auto badge — cascade-generated revision */}
            {isAutoCascade && (
              <Badge variant="outline" className="text-xs bg-neutral-100 text-neutral-600 border-neutral-300 gap-1">
                <Zap className="h-3 w-3" />
                Auto
              </Badge>
            )}

            {/* Reason category */}
            <CategoryBadge
              category={revision.reasonCategory}
              label={revision.reasonCategoryLabel}
            />
          </div>

          {/* Date + author */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {date}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {author}
            </span>
          </div>
        </div>

        {/* ── Reason detail ── */}
        {revision.reason && (
          <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
            {revision.reason}
          </p>
        )}

        {/* ── Impact chips row ── */}
        <div className="flex items-center gap-3 flex-wrap">
          {isCostSales ? (
            <>
              {/* £ budget delta for cost/sales */}
              {budgetDelta !== 0 ? (
                <div
                  className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md ${
                    budgetDelta > 0
                      ? 'bg-accent-warning-50 text-accent-warning-900'
                      : 'bg-accent-success-50 text-accent-success-900'
                  }`}
                >
                  {budgetDelta > 0 ? (
                    <TrendingUp className="h-3.5 w-3.5" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5" />
                  )}
                  {budgetDelta > 0 ? '+' : ''}
                  £{Math.abs(budgetDelta).toLocaleString()}
                </div>
              ) : (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Minus className="h-3.5 w-3.5" />
                  No budget change
                </span>
              )}

              {/* Cascade source link — links back to the resource revision that triggered this */}
              {isAutoCascade && linkedRBId && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Link className="h-3 w-3" />
                  Source: RB-v{linkedRBId}
                </span>
              )}
            </>
          ) : (
            <>
              {/* Hours delta for resource */}
              {hoursDelta !== 0 ? (
                <div
                  className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md ${
                    hoursDelta > 0
                      ? 'bg-accent-warning-50 text-accent-warning-900'
                      : 'bg-accent-success-50 text-accent-success-900'
                  }`}
                >
                  {hoursDelta > 0 ? (
                    <TrendingUp className="h-3.5 w-3.5" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5" />
                  )}
                  {hoursDelta > 0 ? '+' : ''}
                  {hoursDelta.toLocaleString()}h
                </div>
              ) : (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Minus className="h-3.5 w-3.5" />
                  No hours change
                </span>
              )}

              {/* Cascade pills — only shown on resource budget cards */}
              {cascade?.costBudgetUpdated && (
                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-accent-nile-50 text-accent-nile-900 font-medium">
                  Cost cascaded
                </span>
              )}
              {cascade?.salesBudgetUpdated && (
                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-accent-purple-50 text-accent-purple-900 font-medium">
                  Sales cascaded
                </span>
              )}
            </>
          )}
        </div>

        {/* ── Expandable role changes (resource layer only) ── */}
        {hasRoleChanges && (
          <div className="border rounded-md overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted text-sm font-medium transition-colors"
              onClick={() => setRoleChangesOpen((v) => !v)}
            >
              <span className="flex items-center gap-2">
                <Hammer className="h-3.5 w-3.5 text-muted-foreground" />
                Role Changes
                <Badge variant="secondary" className="text-xs">
                  {revision.roleChanges!.filter((c) => c.changeType !== 'unchanged').length}
                </Badge>
              </span>
              {roleChangesOpen ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {roleChangesOpen && (
              <div className="p-0">
                <RoleChangesTable changes={revision.roleChanges!} />
              </div>
            )}
          </div>
        )}

        {/* ── Expandable category changes (cost/sales layers only) ── */}
        {hasCategoryChanges && (
          <div className="border rounded-md overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted text-sm font-medium transition-colors"
              onClick={() => setCategoryChangesOpen((v) => !v)}
            >
              <span className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                Category Changes
                <Badge variant="secondary" className="text-xs">
                  {revision.categoryChanges!.filter((c) => c.changeType !== 'unchanged').length}
                </Badge>
              </span>
              {categoryChangesOpen ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {categoryChangesOpen && (
              <div className="p-0">
                <CategoryChangesTable changes={revision.categoryChanges!} />
              </div>
            )}
          </div>
        )}

        {/* ── Footer buttons ── */}
        <div className="flex items-center gap-2 pt-1 border-t flex-wrap">
          <Button variant="outline" size="sm" onClick={onView}>
            <Eye className="h-4 w-4 mr-1.5" />
            View This Revision
          </Button>
          <Button variant="outline" size="sm" onClick={onCompare}>
            <GitCompare className="h-4 w-4 mr-1.5" />
            Compare
          </Button>
          {onDuplicate && (
            <Button variant="outline" size="sm" onClick={onDuplicate}>
              <Copy className="h-4 w-4 mr-1.5" />
              Duplicate
            </Button>
          )}
          {/* Draft revisions → Make Active only */}
          {revision.status === 'draft' && (
            <Button
              size="sm"
              className="bg-[var(--accent-success-500)] hover:bg-[var(--accent-success-900)] text-white"
              onClick={onMakeActive ?? onRestore}
            >
              Make Active
            </Button>
          )}

          {/* Historical/baseline revisions → Restore only */}
          {(revision.status === 'historical' || revision.status === 'baseline') && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRestore}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Restore
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
